import type { ButtonHTMLAttributes } from 'preact';
import { Button } from './ui/button';

export type VarianteBouton = 'principal' | 'secondaire' | 'danger';
export type TailleBouton = 'defaut' | 'petit' | 'tactile';

// Types importés depuis `preact` directement : leurs homonymes de la surface de
// compatibilité React sont dépréciés, et le moteur ici est bien Preact.
export interface ProprietesBouton extends Omit<ButtonHTMLAttributes, 'size'> {
  readonly variante?: VarianteBouton;
  /**
   * `tactile` porte les 48 px exigés sur le portail et la PWA — doigts abîmés,
   * écran rayé, usage en mouvement. Le poste agent, travaillé à la souris toute
   * la journée, garde la densité par défaut.
   */
  readonly taille?: TailleBouton;
  /** Bloque le bouton et annonce l'attente : évite le double envoi d'une action. */
  readonly enCours?: boolean;
}

const VARIANTE = {
  principal: 'default',
  secondaire: 'outline',
  danger: 'destructive',
} as const;

const TAILLE = {
  defaut: 'default',
  petit: 'sm',
  tactile: 'tactile',
} as const;

/**
 * Bouton du système.
 *
 * Enveloppe le `Button` de `composants/ui`, dont il ne fait que traduire le
 * vocabulaire : l'application parle « principal / secondaire / danger », shadcn
 * parle « default / outline / destructive ». Garder cette couche évite d'avoir à
 * reprendre tous les appels du dépôt à chaque évolution de l'amont, et laisse le
 * nommage du code métier en français.
 *
 * `type="button"` par défaut : dans un formulaire, un bouton sans type soumet,
 * ce qui déclenche des envois non voulus.
 */
export function Bouton({
  variante = 'secondaire',
  taille = 'defaut',
  enCours = false,
  disabled,
  children,
  type,
  ...reste
}: ProprietesBouton) {
  return (
    <Button
      type={type ?? 'button'}
      variant={VARIANTE[variante]}
      size={TAILLE[taille]}
      disabled={disabled === true || enCours}
      aria-busy={enCours}
      {...reste}
    >
      {enCours ? 'Envoi en cours…' : children}
    </Button>
  );
}
